import { useMemo } from 'react';
import { useDataLayer, pushEvent } from '@CS/hooks';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { useRive, Layout, Fit } from '@rive-app/react-canvas';
import bgPatternRive from '@/assets/rive/bgpattern.riv?url';

import { MCDLogo } from '@/assets/images';
import { useNavigateQuery } from '@/hooks';
import { getCampaignTranslations } from '@/redux/campaign/selectors';
import type { TeamId } from '@/utils/quizQueue';
import BaseButton from '@/components/BaseButton/BaseButton';
import ScoreBar from '@/components/ScoreBar/ScoreBar';

import styles from './GameResult.module.css';

type WinnerId = TeamId | 'draw';

type GameResultProps = {
  translations: Record<string, string>;
};

const HOME_ROUTE = '/';
const INTRO_READY_ROUTE = '/intro-ready';

const TRENDING_BACKGROUND =
  'linear-gradient(to bottom, var(--teamTrending) 0%, var(--teamTrending) 100%)';
const TELETEKST_BACKGROUND =
  'linear-gradient(to bottom, var(--teamTeletekst) 0%, var(--teamTeletekst) 100%)';
const DRAW_BACKGROUND =
  'linear-gradient(to bottom, var(--teamTrending) 0%, var(--teamTrending) 49.99%, var(--teamTeletekst) 50%, var(--teamTeletekst) 100%)';

const parseNonNegativeInt = (value: string | null): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.trunc(parsed));
};

const resolveWinner = (
  winner: string | null,
  trendingPoints: number,
  teletekstPoints: number
): WinnerId => {
  if (winner === 'trending' || winner === 'teletekst' || winner === 'draw') return winner;
  if (trendingPoints > teletekstPoints) return 'trending';
  if (teletekstPoints > trendingPoints) return 'teletekst';
  return 'draw';
};

const parseResultFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  const trendingPoints = parseNonNegativeInt(params.get('trending'));
  const teletekstPoints = parseNonNegativeInt(params.get('teletekst'));
  const winner = resolveWinner(params.get('winner'), trendingPoints, teletekstPoints);

  return { trendingPoints, teletekstPoints, winner };
};

const getResultTitle = (
  winner: WinnerId,
  translations: Record<string, string>
): string => {
  switch (winner) {
    case 'trending':
      return `${translations.generalteamTrending}, ${translations.gameOvertrendingTop2}`;
    case 'teletekst':
      return `${translations.generalteamTeletekst} ${translations.gameOverteletekstTop2}`;
    default:
      return translations.gameOvernoRealWinner;
  }
};

const getResultBackgroundStyle = (winner: WinnerId): React.CSSProperties => {
  switch (winner) {
    case 'trending':
      return { background: TRENDING_BACKGROUND };
    case 'teletekst':
      return { background: TELETEKST_BACKGROUND };
    default:
      return { background: DRAW_BACKGROUND };
  }
};

const GameResult = ({ translations }: GameResultProps) => {
  const { search } = useLocation();
  const navigate = useNavigateQuery();

  const { trendingPoints, teletekstPoints, winner } = useMemo(
    () => parseResultFromSearch(search),
    [search]
  );

  const { RiveComponent } = useRive({
    src: bgPatternRive,
    stateMachines: 'SM1',
    autoplay: true,
    layout: new Layout({
      fit: Fit.Cover,
    }),
  });

  const SCORE_PAGE_PATH: Record<WinnerId, string> = {
    trending: 'score_gen trending huge w',
    teletekst: 'score_gen teletekst wint joepie',
    draw: 'score_geen echte winner'
  };

  useDataLayer({
    event: 'page_view',
    eventProperties: {
      page_virtual_path: `/fcg26/${SCORE_PAGE_PATH[winner]}`,
      page_type: '/fam_card_game26'
    }
  });

  const title = getResultTitle(winner, translations);
  const backgroundStyle = getResultBackgroundStyle(winner);

  return (
    <main className={styles.screen} style={backgroundStyle}>
      <div className={styles.riveWrapper}>
        <RiveComponent />
      </div>

      <section className={styles.card}>
        <img src={MCDLogo} alt="McDonald's Logo" className={styles.logo} />
        <h1 className={styles.title}>{title}</h1>
        <div className={styles.scoreContainer}>
          <ScoreBar
            trending={trendingPoints}
            teletekst={teletekstPoints}
            questionIndex={0}
            trendingColor="var(--teamTrending)"
            teletekstColor="var(--teamTeletekst)"
            trendingStrokeColor="#0183B2"
            teletekstStrokeColor="#A041AB"
            variant={window.innerWidth <= 380 && window.innerHeight <= 700 ? "gameResultSmall" : "result"}
          />
        </div>
        <div className={styles.actions}>
          <BaseButton
            text={translations.gameOverbackToHome}
            variant="outline"
            onClick={() => {
              pushEvent({
                event: 'button_click',
                eventAction: '/fcg26/terug_naar_home',
                eventLabel: '/fcg26/score',
                team: winner === 'draw' ? undefined : winner
              });
              navigate(HOME_ROUTE, { remove: true });
            }}
          />
          <BaseButton
            text={translations.gameOverplayAgain}
            onClick={() => {
              pushEvent({
                event: 'button_click',
                eventAction: '/fcg26/speel_opnieuw',
                eventLabel: '/fcg26/score',
                team: winner === 'draw' ? undefined : winner
              });
              navigate(INTRO_READY_ROUTE, { remove: true });
            }}
          />
        </div>
      </section>
    </main>
  );
};

const mapStateToProps = (state: any) => ({
  translations: getCampaignTranslations(state)
});

export default connect(mapStateToProps)(GameResult);